import type { Page } from "@nexxt/contracts";
import type {
  GetVendorReturnInput,
  ListVendorReturnsInput,
  VendorReturnActionInput,
  VendorReturnReasonInput,
  vendorReturnDto,
  vendorReturnSummaryDto,
} from "@nexxt/contracts/logistics";
import type * as z from "zod";

// What the vendor return screens ask of a gateway (ADR-229): one call per operation of logistics, with the input and
// the answer of its contract. Each app gives its own, on the client of its gateway, and the screens never see a route.
// The operations are those of ExternalLogisticsService (ADR-227 point 11): reading and answering. Opening,
// withdrawing, Operations' decision and the messages to the systems are Tomorrow's, written by Backstage on top.

export type VendorReturn = z.output<typeof vendorReturnSummaryDto>;
export type VendorReturnDetail = z.output<typeof vendorReturnDto>;
export type VendorReturnLine = VendorReturnDetail["lines"][number];

/** The input of an operation as the screens give it: what the contract coerces (`page`, `pageSize`) as numbers. */
type Given<I> = { [K in keyof I]: unknown extends I[K] ? number | undefined : I[K] };
type Read<I, R> = (input: Given<I>, signal?: AbortSignal) => Promise<R>;
type Write<I, R> = (input: I) => Promise<R>;

/** The operations of the vendor return screens, by the names of logistics' RPC methods. */
export interface LogisticsClient {
  listVendorReturns: Read<ListVendorReturnsInput, Page<VendorReturn>>;
  getVendorReturn: Read<GetVendorReturnInput, VendorReturnDetail>;
  confirmVendorReturn: Write<VendorReturnActionInput, VendorReturnDetail>;
  refuseVendorReturn: Write<VendorReturnReasonInput, VendorReturnDetail>;
}

let registered: LogisticsClient | undefined;

/** Once, when the app starts: before it, no vendor return screen loads (the route loaders read through it too). */
export function registerLogisticsClient(client: LogisticsClient): void {
  registered = client;
}

export function logisticsClient(): LogisticsClient {
  if (!registered) throw new Error("registerLogisticsClient was not called when the app started");
  return registered;
}
